import React, {useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column, ColumnFilterElementTemplateOptions} from "primereact/column";
import {AlertDTO, SeverityDTO} from "@energy-advice/alertfan-api-client";
import {Tooltip} from "react-tooltip";
import {formatTime, primereactDataTableSortDateFunction} from "../../../utils/time-utils";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {useGetAlertFan} from "../../../api/api-hooks/alert-fan-hooks";
import {stringArrayToTags} from "../../basic/stringsArrayToTags/StringArrayToTags";
import ParametersTableBody from "./ParametersTableBody";
import TagsHistory from "./TagsHistory";


type AlertsViewProps = {
    itemsPerPage?: number;
    updateFrequency?: number;

    setTags: (tags: { topics?: Array<string>, items?: Array<string> }) => void
    setTab: (n: number) => void
}

const SeverityMap: Record<SeverityDTO, any> = {
    SEVERE: <i data-tooltip-content={"Severe"} style={{color: "red"}}
               className="pi pi-exclamation-triangle severity-body-tooltip"></i>,
    INFO: <i data-tooltip-content={"Info"} style={{color: "#3B82F6"}}
             className="pi pi-info-circle severity-body-tooltip"></i>,
    DEBUG: <i data-tooltip-content={"Debug"}
              className="fa fa-solid fa-bug severity-body-tooltip"></i>,
    WARNING: <i data-tooltip-content={"Warning"} style={{color: "orange"}}
                className="pi pi-exclamation-triangle severity-body-tooltip"></i>
}


const AlertsView = ({updateFrequency, itemsPerPage, setTags, setTab}: AlertsViewProps) => {
    const {
        data,
        refetch,
        isLoading,
        error
    } = useGetAlertFan(new Date().toISOString(), {refetchInterval: updateFrequency ? updateFrequency * 1000 : undefined, cacheTime: 0});

    const rowsPerPageOptions = itemsPerPage ? [itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
            return a.indexOf(x) == i;
        }) : [10, 20, 30];


    const severityBody = (rowData: AlertDTO) => {
        return <div style={{display: "flex", justifyContent: "center"}}>
            <Tooltip anchorSelect=".severity-body-tooltip" delayShow={200}/>
            {
                rowData.severity ? SeverityMap[rowData.severity] : <></>
            }
        </div>
    }

    const severityFilter = (options: ColumnFilterElementTemplateOptions) => {
        return <div className={"flex flex-column gap-2"}>
            {Object.values(SeverityDTO).map(serverity => {
                return <div key={serverity} className="flex gap-1 align-items-center">
                    <Checkbox inputId={serverity} name="category"
                              value={serverity}
                              onChange={e => options.filterCallback(e.value)}
                              checked={options.value === serverity}
                    />
                    {SeverityMap[serverity]}
                    <label htmlFor={serverity}>{serverity}</label>
                </div>
            })}
        </div>
    }

    const historyAlertBody = (rowData: AlertDTO) => {
        const [historyDialog, setHistoryDialog] = useState(false)
        return <>
            <Button className={"p-button-sm"}
                    icon={"pi pi-history"}
                    onClick={() => setHistoryDialog(true)}
                    tooltip={"Alert History"}
            />
            {historyDialog && <TagsHistory tags={[...rowData.itemTags, ...rowData.topicTags]}
                                           dialog={historyDialog}
                                           showDialog={setHistoryDialog}/>}
        </>
    }

    if (error) {
        return <div style={{border: "1px solid red"}}>Error while fetching alerts...</div>
    }

    return <div className={"flex flex-column gap-2"}>
        <div className={"flex justify-content-end"}>
            <Button icon={"pi pi-refresh"}
                    className={"p-button-sm"}
                    onClick={() => refetch()}
            />
        </div>
        <DataTable value={data} loading={isLoading}
                   paginator={data ? data.length > 10 : false} responsiveLayout="scroll"
                   paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" rows={itemsPerPage || 10}
                   rowsPerPageOptions={rowsPerPageOptions}
                   removableSort size={"small"}
        >
            <Column header={"Severity"}
                    field={"severity"}
                    style={{width: 0}}
                    sortable
                    filter
                    filterMatchMode={"contains"}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterElement={severityFilter}
                    body={severityBody}
            />
            <Column header={"Text"}
                    field={"text"}
                    sortable
                    filter
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={"Search"}
                    filterField="text"
                    filterMatchMode={"contains"}
            />
            <Column header={"Topic Tags"}
                    field={"topicTags"}
                    body={(e: AlertDTO) => stringArrayToTags(e.topicTags)}
                    filter
                    filterMatchMode={"contains"}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={"Tag name"}
            />
            <Column header={"Item Tags"}
                    field={"itemTags"}
                    body={(e: AlertDTO) => stringArrayToTags(e.itemTags)}
                    filter
                    filterMatchMode={"contains"}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={"Tag name"}
            />
            <Column header={"Started"}
                    field={"startsAt"}
                    body={(rowData) => formatTime(rowData.startsAt)}
                    sortable
                    sortFunction={primereactDataTableSortDateFunction}
            />
            <Column style={{width: 0}} body={(rowData) => <ParametersTableBody properties={rowData.properties}/>}/>
            <Column style={{width: "0"}}
                    body={(rowData) => <Button icon={"pi pi-plus"}
                                               className={"p-button-sm"}
                                               onClick={() => {
                                                   setTab(1)
                                                   setTags({
                                                       items: rowData.itemTags,
                                                       topics: rowData.topicTags
                                                   })
                                               }}
                                               tooltip={"Create Silence"}
                    />
                    }
            />
            <Column body={historyAlertBody} style={{width: "0"}}/>
        </DataTable>
    </div>
};

export default AlertsView;
